import React, { memo, useEffect } from "react";
import { Card, CardBody, Row, Col, Button, UncontrolledAlert, ButtonToolbar, Spinner, Alert } from "reactstrap";
import { Field, reduxForm, clearFields, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { MdArrowBack, MdRefresh } from "react-icons/md";
import { Link } from "react-router-dom";
import { renderField } from "../../../../utils/renderfield";
import renderSelectField from "../../../../shared/components/form/Select";
import { getIssuerConfig, resetSCPConfigs } from "../../../SCPConfigurations/actions/scpActions";
import { getEmvProfile, resetEMVConfigs } from "../../../SCPConfigurations/actions/emvProfileCode.action";
import { getPersoFormatCode, resetPersoConfigs } from "../../../SCPConfigurations/actions/getPersoFormatCode.actions";
import { resetPost } from "../../actions/issuerConfig.actions";
import validate from "./validate";
import AccessControl from "../../../../shared/components/AccessControl";
import { permissionsConstants } from "../../../../constants/permissions.constants";
import SCPIssuersDropDown from "../../../Issuers/SCPIssuersDropDown";

let ConfigurationForm = memo((props) => {
    const { handleSubmit, dispatch, reset, pristine, invalid, submitting, scpConfiguration, config, createIssuerConfig, configId, issuerCode, EmvProfileCodes, PersoFormatCodes, pinGenerationMethod, persoCode, cardProfileCode, permissions, selectedIssuer, change } = props;

    const userPermissions = permissions && permissions.map((permission) => permission.authority);

    const isAdmin = userPermissions && userPermissions.includes(permissionsConstants.FETCH_ISSUER_CONFIGS);
    const issuer = isAdmin ? selectedIssuer : issuerCode;

    const getConfigFromIssuer = (selectedIssuer) => {
        reset();
        if (selectedIssuer) {
            dispatch(resetEMVConfigs());
            dispatch(resetPersoConfigs());
            dispatch(getIssuerConfig(selectedIssuer));
        }
    };

    const foundConfig = config && config.response && config.response.data && config.response.data.length && !config.loading;

    const getConfig = () => {
      
        reset();
        if (issuer) {
            
            dispatch(resetEMVConfigs());
            dispatch(resetPersoConfigs());
            dispatch(getIssuerConfig(issuer));
        }
    };

    useEffect(() => {
        if (!configId) {
            dispatch(clearFields("issuerConfiguration_form", true, true, "emvProfileCode"));
        }

        if (cardProfileCode && cardProfileCode.name) {
            dispatch(getEmvProfile(issuer, cardProfileCode.name));
        }
    }, [cardProfileCode, dispatch, issuer]);

    useEffect(() => {
        if (!configId) {
            dispatch(clearFields("issuerConfiguration_form", true, true, "persoFormatCode"));
        }

        if (persoCode && persoCode.persoCode) {
            dispatch(getPersoFormatCode(persoCode.persoId));
        }
    }, [dispatch, persoCode]);

    useEffect(() => {
        if (!isAdmin || issuer) {
            dispatch(getIssuerConfig(issuer));
        }
        return () => dispatch(resetSCPConfigs());
    }, [dispatch, issuer, isAdmin]);

    useEffect(() => {
        dispatch(resetPost());
        return () => {
            dispatch(resetPost());
        };
    }, [dispatch]);

    useEffect(() => {
        if (configId && config && config.response && config.response.data && config.response.data.length) {
           
            if (config.response.data[0].pinGenerationMethod === "default_pin") {
                dispatch(change("pinGenerationMethod", { name: "Default", value: config.response.data[0].pinGenerationMethod }));
            } else {
                dispatch(change("pinGenerationMethod", { name: "Random", value: config.response.data[0].pinGenerationMethod }));
            }

            dispatch(change("name", config.response.data[0].name));
            dispatch(change("description", config.response.data[0].description));
            dispatch(change("cardProgram", config.response.data[0].cardProgram));
            dispatch(change("cardScheme", config.response.data[0].cardScheme));
            dispatch(change("cardProfileCode", config.response.data[0].cardProfileCode));
            dispatch(change("emvProfileCode", config.response.data[0].emvProfileCode));
            dispatch(change("persoCode", config.response.data[0].persoCode));
            dispatch(change("persoFormatCode", config.response.data[0].persoFormatCode));
        }
    }, [configId, config]);

    return (
        <Col md={12} lg={12}>
            <Card>
                <CardBody>
                    <div className="card__title">
                        <h5 className="bold-text">
                            <Link to="/issuers/config" id="link-all-configs">
                                <MdArrowBack size={20} /> Back to Configurations
                            </Link>
                        </h5>
                    </div>
                    {configId && config && config.loading ? (
                        <div className="text-center">
                            <Spinner color="success" className="my-4" style={{ width: "6rem", height: "6rem" }} />
                            <h4 className="text-secondary">Fetching configuration with name ({configId})</h4>
                        </div>
                    ) : (
                        <div>
                            {configId && !foundConfig ? (
                                <h4 className="text-danger">{config && config.error ? `Something went wrong. Could not fetch configuration with name (${configId})` : `Configutation with name (${configId}) not found`}</h4>
                            ) : (
                                <form className="form" onSubmit={handleSubmit}>
                                    {scpConfiguration && scpConfiguration.error && scpConfiguration.error.errors && scpConfiguration.error.errors.length ? (
                                        <UncontrolledAlert color="danger">
                                            <h5 className="font-weight-bold">Please check the following fields for errors</h5>
                                            {scpConfiguration.error.errors.map((err) => (
                                                <p>
                                                    <strong>{err.fieldName}:</strong> {err.message}
                                                </p>
                                            ))}
                                        </UncontrolledAlert>
                                    ) : null}
                                    {scpConfiguration && scpConfiguration.error ? (
                                        <Alert color="danger">
                                            Could not fetch Smart-Card Configuration setting.
                                            {scpConfiguration.loading ? <Spinner className="float-right" size="sm" color="danger" /> : <MdRefresh onClick={getConfig} id="fetc-scp-config" className="float-right pointer" size={20} />}
                                        </Alert>
                                    ) : null}
                                    <Row>
                                        <Col lg="4">
                                            <AccessControl allowedPermissions={[permissionsConstants.FETCH_ISSUER_CONFIGS]} renderNoAccess={() => null}>
                                                {configId && foundConfig ? null : <SCPIssuersDropDown required disabled={configId} id="issuer" label="Select Issuer" onChange={getConfigFromIssuer} />}
                                            </AccessControl>
                                        </Col>
                                    </Row>
                                    <h5 className="font-weight-bold">Configuration Information</h5>
                                    <hr className="my-3" />
                                    <Row>
                                        <Col lg="4">
                                            {configId && foundConfig ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label ">Name</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="name" name="name" component={renderField} type="text" disabled={true} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label required">Name</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="name" name="name" component={renderField} type="text" placeholder="name" />
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                        <Col lg="8">
                                            <div className="form__form-group">
                                                <span className="form__form-group-label required">Description</span>
                                                <div className="form__form-group-field">
                                                    <Field id="description" name="description" component={renderField} type="text" placeholder="Configuration description" />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        {/* <Col lg="4">
                      <div className="form__form-group">
                        <span className="form__form-group-label required">
                          Request Type
                        </span>
                        <div className="form__form-group-field">
                          <Field
                            id="requestType"
                            name="requestType"
                            component={renderSelectField}
                            options={[{ type: "NEW" }, { type: "REISSUE" }]}
                            valueKey="type"
                            labelKey="type"
                            placeholder="request type"
                          />
                        </div>
                      </div>
                    </Col> */}
                                        <Col lg="4">
                                            {configId && foundConfig ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label">Card Program</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="cardProgram" name="cardProgram" component={renderField} type="text" disabled={true} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label required">Card Program</span>
                                                    <div className="form__form-group-field">
                                                        <Field
                                                            id="cardProgram"
                                                            name="cardProgram"
                                                            component={renderSelectField}
                                                            options={(scpConfiguration && scpConfiguration.response && scpConfiguration.response.data && scpConfiguration.response.data[0].cardPrograms) || []}
                                                            valueKey="name"
                                                            labelKey="name"
                                                            placeholder="card program"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                        {/* <Col lg="4">
                      <div className="form__form-group">
                        <span className="form__form-group-label required">
                          Scp Card Program
                        </span>
                        <div className="form__form-group-field">
                          <Field
                            id="scpCardProgram"
                            name="scpCardProgram"
                            component={renderSelectField}
                            options={
                              (scpConfiguration &&
                                scpConfiguration.response &&
                                scpConfiguration.response.data &&
                                scpConfiguration.response.data[0]
                                  .scpCardPrograms) ||
                              []
                            }
                            valueKey="name"
                            labelKey="name"
                            placeholder="scp card program"
                          />
                        </div>
                      </div>
                    </Col> */}
                                        <Col lg="4">
                                            {configId && foundConfig ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label">Card Scheme</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="cardScheme" name="cardScheme" component={renderField} type="text" disabled={true} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label required">Card Scheme</span>
                                                    <div className="form__form-group-field">
                                                        <Field
                                                            id="cardScheme"
                                                            name="cardScheme"
                                                            component={renderSelectField}
                                                            options={
                                                                (scpConfiguration &&
                                                                    scpConfiguration.response &&
                                                                    scpConfiguration.response.data &&
                                                                    scpConfiguration.response.data[0].cardScheme &&
                                                                    scpConfiguration.response.data[0].cardScheme.map((e) => ({
                                                                        value: e,
                                                                        label: e,
                                                                    }))) ||
                                                                []
                                                            }
                                                            valueKey="value"
                                                            labelKey="label"
                                                            placeholder="select card program"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                    <h5 className="font-weight-bold">Card Profile Information</h5>
                                    <hr className="my-3" />
                                    <Row>
                                        <Col lg="4">
                                            {configId && foundConfig ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label">Card Profile Code</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="cardProfileCode" name="cardProfileCode" component={renderField} type="text" disabled={true} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label required">Card Profile Code</span>
                                                    <div className="form__form-group-field">
                                                        <Field
                                                            id="cardProfileCode"
                                                            name="cardProfileCode"
                                                            component={renderSelectField}
                                                            options={(scpConfiguration && scpConfiguration.response && scpConfiguration.response.data && scpConfiguration.response.data[0].cardProfiles) || []}
                                                            valueKey="name"
                                                            labelKey="name"
                                                            placeholder="card profile code"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                        <Col lg="4">
                                            {configId && foundConfig ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label">Emv Profile Code</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="emvProfileCode" name="emvProfileCode" component={renderField} type="text" disabled={true} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label required">Emv Profile Code</span>
                                                    <div className="form__form-group-field">
                                                        <Field
                                                            id="emvProfileCode"
                                                            name="emvProfileCode"
                                                            component={renderSelectField}
                                                            options={(EmvProfileCodes && EmvProfileCodes.response && EmvProfileCodes.response.data) || []}
                                                            valueKey="name"
                                                            labelKey="name"
                                                            placeholder="select card profile code"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                    <h5 className="font-weight-bold">Perso Information</h5>
                                    <hr className="my-3" />
                                    <Row>
                                        <Col lg="4">
                                            {configId && foundConfig ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label">Perso Code</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="persoCode" name="persoCode" component={renderField} type="text" disabled={true} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label required">Perso Code</span>
                                                    <div className="form__form-group-field">
                                                        <Field
                                                            id="persoCode"
                                                            name="persoCode"
                                                            component={renderSelectField}
                                                            options={(scpConfiguration && scpConfiguration.response && scpConfiguration.response.data && scpConfiguration.response.data[0].persos) || []}
                                                            valueKey="persoCode"
                                                            labelKey="persoCode"
                                                            placeholder="perso code"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                        <Col lg="4">
                                            {configId && foundConfig ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label ">Perso Format Code</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="persoFormatCode" name="persoFormatCode" component={renderField} type="text" disabled={true} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label required">Perso Format Code</span>
                                                    <div className="form__form-group-field">
                                                        <Field
                                                            id="persoFormatCode"
                                                            name="persoFormatCode"
                                                            component={renderSelectField}
                                                            options={(PersoFormatCodes && PersoFormatCodes.response && PersoFormatCodes.response.data) || []}
                                                            valueKey="name"
                                                            labelKey="name"
                                                            placeholder="select perso code"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                    <Row />
                                    <h5 className="font-weight-bold">Pin Configuration</h5>
                                    <hr className="my-3" />
                                    <Row>
                                        <Col lg="4">
                                            {configId && foundConfig ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label ">Pin Generation Method</span>
                                                    <div className="form__form-group-field">
                                                        <Field
                                                            id="pinGenerationMethod"
                                                            name="pinGenerationMethod"
                                                            component={renderSelectField}
                                                            options={[
                                                                { name: "Random", value: "random_pin" },
                                                                { name: "Default", value: "default_pin" },
                                                            ]}
                                                            valueKey="name"
                                                            labelKey="value"
                                                            placeholder="pin generation method"
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label required">Pin Generation Method</span>
                                                    <div className="form__form-group-field">
                                                        <Field
                                                            id="pinGenerationMethod"
                                                            name="pinGenerationMethod"
                                                            component={renderSelectField}
                                                            options={[
                                                                { name: "Random", value: "random_pin" },
                                                                { name: "Default", value: "default_pin" },
                                                            ]}
                                                            valueKey="name"
                                                            labelKey="value"
                                                            placeholder="pin generation method"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Col>

                                        <Col lg="4">
                                            {configId && foundConfig && config && config.response && config.response.data && config.response.data[0] && config.response.data[0].pinGenerationMethod ? (
                                                pinGenerationMethod && pinGenerationMethod.name === "Default" ? (
                                                    <div className="form__form-group">
                                                        <span className="form__form-group-label">Default Pin Value (Defaults to '0000')</span>
                                                        <div className="form__form-group-field">
                                                            <Field id="defaultPinValue" name="defaultPinValue" component={renderField} type="text" placeholder="pin value" disabled={true} />
                                                        </div>
                                                    </div>
                                                ) : null
                                            ) : pinGenerationMethod && pinGenerationMethod.name === "Default" ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label">Default Pin Value (Defaults to '0000')</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="defaultPinValue" name="defaultPinValue" component={renderField} type="text" placeholder="pin value" />
                                                    </div>
                                                </div>
                                            ) : null}
                                        </Col>
                                    </Row>

                                    <ButtonToolbar className="form__button-toolbar">
                                        {configId && foundConfig ? null : (
                                            <Button type="button" id="btn-reset-config" onClick={reset} disabled={pristine || submitting}>
                                            Reset
                                            </Button>
                                        )}
                                        <Button color="primary" type="submit" id="btn-submit-config" disabled={submitting || invalid}>
                                            {createIssuerConfig && createIssuerConfig.loading ? (
                                                <span>
                                                    <Spinner size="sm" color="default" />{" "}
                                                </span>
                                            ) : null}
                                            Submit
                                        </Button>
                                    </ButtonToolbar>
                                </form>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>
        </Col>
    );
});

ConfigurationForm.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
};

ConfigurationForm = reduxForm({
    form: "issuerConfiguration_form",
    enableReinitialize: true,
    validate,
})(ConfigurationForm);

const selector = formValueSelector("issuerConfiguration_form");

ConfigurationForm = connect((state) => ({
    cardProfileCode: selector(state, "cardProfileCode"),
    pinGenerationMethod: selector(state, "pinGenerationMethod"),
    persoCode: selector(state, "persoCode"),
    selectedIssuer: selector(state, "scpIssuers"),
    permissions: state.permissions && state.permissions.response,

    createIssuerConfig: state.createIssuerConfig,
    scpConfiguration: state.getAllScpIssuerConfig,
    EmvProfileCodes: state.getAllScpEmvProfileCode,
    PersoFormatCodes: state.getAllPersoFormatCode,
    issuerCode: state.getIssuerDomainMapping && state.getIssuerDomainMapping.response && state.getIssuerDomainMapping.response.data ? state.getIssuerDomainMapping.response.data[0] : [],
}))(ConfigurationForm);

export default ConfigurationForm;
